/**
 * DFT-based bpm estimator for rPPG pulse signals.
 *
 * We use a direct O(N·K) discrete Fourier transform restricted to the
 * heart-rate band (42-180 bpm → 0.7-3.0 Hz). For N≈300 samples and ~60
 * frequency bins in that band, this is ~18k multiply-adds per estimate —
 * tiny compared to the MediaPipe face detection running alongside — so a
 * proper FFT library is not worth the bundle weight.
 *
 * Signal is mean-detrended and Hann-windowed before the DFT to reduce
 * spectral leakage. The peak bin is converted back to beats per minute.
 */

const BPM_MIN = 42;
const BPM_MAX = 180;

export interface BpmEstimate {
  bpm: number;
  /** Peak power at bpm (relative, not calibrated) */
  power: number;
  /**
   * Signal-to-noise ratio in [0, 1]: ratio of peak-bin power to the total
   * in-band power. Higher = cleaner pulse. Values above 0.15 are usually
   * reliable on clean recordings.
   */
  snr: number;
}

/**
 * Estimate heart rate (bpm) from a pulse signal sampled at `fps` Hz.
 * Returns `null` if the signal is too short or the peak SNR is too low
 * to be trusted.
 *
 * @param signal Pulse signal samples (ideally 8+ seconds worth)
 * @param fps    Sampling rate in frames per second
 */
export function estimateBpm(signal: Float32Array, fps: number): BpmEstimate | null {
  const N = signal.length;
  // Need at least ~6 seconds of data — shorter windows produce coarse
  // frequency bins and noisy estimates.
  if (N < Math.ceil(fps * 6)) return null;

  // 1. Detrend (subtract mean)
  let mean = 0;
  for (let i = 0; i < N; i++) mean += signal[i];
  mean /= N;

  // 2. Apply Hann window to reduce spectral leakage
  const x = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    const w = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (N - 1)));
    x[i] = (signal[i] - mean) * w;
  }

  // 3. DFT restricted to bpm band
  const fMin = BPM_MIN / 60;
  const fMax = BPM_MAX / 60;
  const kMin = Math.max(1, Math.floor((fMin * N) / fps));
  const kMax = Math.min(N / 2, Math.ceil((fMax * N) / fps));

  let bestK = -1;
  let bestPower = 0;
  let totalPower = 0;

  for (let k = kMin; k <= kMax; k++) {
    let real = 0;
    let imag = 0;
    const w = (2 * Math.PI * k) / N;
    for (let n = 0; n < N; n++) {
      const theta = -w * n;
      real += x[n] * Math.cos(theta);
      imag += x[n] * Math.sin(theta);
    }
    const power = real * real + imag * imag;
    totalPower += power;
    if (power > bestPower) {
      bestPower = power;
      bestK = k;
    }
  }

  if (bestK < 0 || totalPower <= 0) return null;

  // SNR relative to total in-band power (higher is cleaner)
  const snr = bestPower / totalPower;

  // Reject peaks that are basically noise
  if (snr < 0.08) return null;

  // Convert bin index → frequency (Hz) → bpm
  const freq = (bestK * fps) / N;
  const bpm = freq * 60;

  return { bpm, power: bestPower, snr };
}
